
import React, { memo } from 'react';
import { ContentItem } from '@/data/ratgeberArticles';
import { useLanguage } from '@/contexts/LanguageContext';
import { RatgeberCTA } from './RatgeberCTA';

// Memoized Paragraph Component für effizienteres Rendering
const MemoizedParagraph = memo(({ text, t }: { text: string, t: (key: string) => string }) => (
  <p className="text-gray-700 leading-relaxed">
    {t(text) === text ? text : t(text)}
  </p>
));

// Memoized Subtitle Component
const MemoizedSubtitle = memo(({ text, t }: { text: string, t: (key: string) => string }) => (
  <h3 className="text-xl font-semibold mt-6 mb-3 text-primary">
    {t(text) === text ? text : t(text)}
  </h3>
));

// Memoized List Component
const MemoizedList = memo(({ text, t }: { text: string, t: (key: string) => string }) => (
  <ul className="list-disc pl-5 space-y-2 text-gray-700">
    {text.split('\n').map((line, lineIndex) => (
      <li key={lineIndex} className="pl-1 marker:text-primary">
        {line.startsWith('•') 
          ? (t(line.substring(1).trim()) === line.substring(1).trim() 
              ? line.substring(1).trim() 
              : t(line.substring(1).trim())) 
          : (t(line) === line ? line : t(line))}
      </li>
    ))}
  </ul>
));

// Memoized Scientific Component
const MemoizedScientific = memo(({ text, t }: { text: string, t: (key: string) => string }) => (
  <div className="bg-blue-50 p-4 rounded-md border border-blue-100 my-4">
    <p className="text-blue-800 italic">
      {t(text) === text ? text : t(text)}
    </p>
    <div className="mt-2 flex justify-end">
      <span className="text-xs text-blue-600 font-semibold">{t('scientificInsight')}</span>
    </div>
  </div>
));

// Hauptkomponente mit Memoization
const ArticleContent: React.FC<{ 
  content: ContentItem[];
  cityKeywords?: string[];
  showCTA?: boolean;
}> = memo(({ content, cityKeywords = [], showCTA = true }) => {
  const { t } = useLanguage();
  
  // Find a good position for the CTA (after first 2-3 content items)
  const ctaPosition = Math.min(3, Math.floor(content.length / 2));
  
  return (
    <div className="space-y-4">
      {content.map((item, index) => {
        const contentElement = (() => {
          switch(item.type) {
            case 'paragraph':
              return <MemoizedParagraph key={index} text={item.text} t={t} />;
            case 'subtitle':
              return <MemoizedSubtitle key={index} text={item.text} t={t} />;
            case 'list':
              return <MemoizedList key={index} text={item.text} t={t} />;
            case 'scientific':
              return <MemoizedScientific key={index} text={item.text} t={t} />;
            default:
              return null;
          }
        })();
        
        return (
          <React.Fragment key={index}>
            {contentElement}
            {/* Insert CTA after first few items */}
            {showCTA && index === ctaPosition && (
              <RatgeberCTA 
                cityKeywords={cityKeywords}
                title="Professionelle Hundeschulen in Ihrer Nähe"
              />
            )}
          </React.Fragment>
        );
      })}
      
      {/* General CTA at the end */}
      {showCTA && (
        <RatgeberCTA 
          title="Jetzt die passende Hundeschule finden"
        />
      )}
    </div>
  );
});

export default ArticleContent;
